import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { EXAMEN_BLANC_SECTIONS, ELIMINATOIRES, TOTAL_POSSIBLE, computeScore, getVerdict } from '../data/examenBlancEleve';
import { ClipboardCheck, CheckCircle2, AlertTriangle, XCircle, RotateCcw, ArrowLeft, GraduationCap, Trophy, Lock } from 'lucide-react';

const STATES = [
  { v: 'acquis', l: 'Acquis', color: 'bg-emerald-600 text-white border-emerald-500', icon: CheckCircle2 },
  { v: 'progres', l: 'En progrès', color: 'bg-amber-500 text-amber-950 border-amber-400', icon: AlertTriangle },
  { v: 'aRevoir', l: 'À revoir', color: 'bg-red-600 text-white border-red-500', icon: XCircle },
];

const COLOR_MAP = {
  sky: 'border-sky-700/40 text-sky-300 bg-sky-600/10',
  amber: 'border-amber-700/40 text-amber-300 bg-amber-600/10',
  red: 'border-red-700/40 text-red-300 bg-red-600/10',
  emerald: 'border-emerald-700/40 text-emerald-300 bg-emerald-600/10',
};

const VERDICT_COLOR = {
  emerald: 'from-emerald-700/30 via-emerald-900/10 border-emerald-700/40 text-emerald-300',
  sky: 'from-sky-700/30 via-sky-900/10 border-sky-700/40 text-sky-300',
  amber: 'from-amber-700/30 via-amber-900/10 border-amber-700/40 text-amber-300',
  red: 'from-red-700/30 via-red-900/10 border-red-700/40 text-red-300',
};

function CritereRow({ critere, value, onChange }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-4 flex items-center gap-4 flex-wrap" data-testid={`critere-${critere.id}`}>
      <div className="flex-1 min-w-[240px]">
        <div className="text-white text-[14px] leading-snug">{critere.label}</div>
        {critere.coef > 1 && <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-400/80 font-bold font-mono-spec mt-1">Coef ×{critere.coef}</div>}
      </div>
      <div className="flex gap-1.5">
        {STATES.map((s) => {
          const Icon = s.icon;
          const active = value === s.v;
          return (
            <button
              key={s.v}
              onClick={() => onChange(critere.id, s.v)}
              data-testid={`critere-${critere.id}-${s.v}`}
              className={`h-9 px-3 rounded-md border text-[11.5px] font-bold inline-flex items-center gap-1.5 transition ${active ? s.color + ' shadow-md' : 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white'}`}
            >
              <Icon size={13} /> {s.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ExamenBlancElevePage() {
  const [results, setResults] = useState({});
  const [eliminatoires, setEliminatoires] = useState({});

  const totalCriteres = useMemo(() => EXAMEN_BLANC_SECTIONS.reduce((acc, s) => acc + s.criteres.length, 0), []);
  const evaluated = Object.keys(results).length;
  const pct = totalCriteres > 0 ? Math.round((evaluated / totalCriteres) * 100) : 0;
  const score = computeScore(results);
  const hasEliminatoire = Object.values(eliminatoires).some(Boolean);
  const verdict = getVerdict(score, TOTAL_POSSIBLE, hasEliminatoire);

  const setResult = (id, v) => setResults((prev) => ({ ...prev, [id]: v }));
  const toggleEliminatoire = (i) => setEliminatoires((prev) => ({ ...prev, [i]: !prev[i] }));
  const reset = () => {
    if (!window.confirm('Réinitialiser toute la simulation ?')) return;
    setResults({});
    setEliminatoires({});
  };

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-10 mb-16" data-testid="examen-blanc-eleve-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/cours-interactifs" className="hover:text-white transition">Cours interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Examen blanc · Élève</span>
        </nav>

        {/* Hero */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-9 mb-7 sm:mb-10 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-red-600/10 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
              <ClipboardCheck size={13} className="text-red-400" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Examen blanc · Auto-évaluation</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[34px] sm:text-[44px] lg:text-[54px] leading-[0.98] tracking-tight">
              Es-tu prêt(e)<br />
              <span className="text-red-500">pour le jour J ?</span>
            </h1>
            <p className="text-white/70 text-[15px] lg:text-[16px] mt-5 max-w-2xl leading-relaxed">
              Évalue-toi sur les 5 sections de l'examen pratique du permis B. À chaque critère, sois honnête : <span className="text-emerald-300 font-semibold">Acquis</span>, <span className="text-amber-300 font-semibold">En progrès</span> ou <span className="text-red-300 font-semibold">À revoir</span>. Tu obtiens un verdict immédiat et chartré.
            </p>

            {/* Stats */}
            <div className="mt-7 grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-white/10 bg-[#0a0a0b] p-3.5 text-center">
                <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Évalué</div>
                <div className="font-serif-editorial text-white text-[28px] leading-none mt-1">{evaluated}<span className="text-white/30 text-[16px]">/{totalCriteres}</span></div>
                <div className="text-[10.5px] text-white/40 mt-1 font-mono-spec">{pct} %</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#0a0a0b] p-3.5 text-center">
                <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Score</div>
                <div className="font-serif-editorial text-red-500 text-[28px] leading-none mt-1">{score}<span className="text-white/30 text-[16px]">/{TOTAL_POSSIBLE}</span></div>
                <div className="text-[10.5px] text-white/40 mt-1 font-mono-spec">pondéré</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#0a0a0b] p-3.5 text-center">
                <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Éliminatoires</div>
                <div className="font-serif-editorial text-amber-400 text-[28px] leading-none mt-1">{Object.values(eliminatoires).filter(Boolean).length}</div>
                <div className="text-[10.5px] text-white/40 mt-1 font-mono-spec">cochés</div>
              </div>
            </div>
          </div>
        </div>

        {/* Sections */}
        {EXAMEN_BLANC_SECTIONS.map((sec) => (
          <section key={sec.id} className="mb-10" data-testid={`section-${sec.id}`}>
            <div className={`rounded-2xl border ${COLOR_MAP[sec.color]} p-5 mb-4`}>
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold font-mono-spec mb-1">{sec.criteres.length} critère{sec.criteres.length > 1 ? 's' : ''}</div>
              <h2 className="font-serif-editorial text-white text-[24px] leading-tight">{sec.label}</h2>
              <p className="text-white/70 text-[13.5px] mt-2 leading-relaxed">{sec.intro}</p>
            </div>
            <div className="space-y-2">
              {sec.criteres.map((c) => (
                <CritereRow key={c.id} critere={c} value={results[c.id]} onChange={setResult} />
              ))}
            </div>
          </section>
        ))}

        {/* Éliminatoires */}
        <section className="mb-10" data-testid="eliminatoires-section">
          <div className="rounded-2xl border border-red-700/30 bg-red-950/15 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lock size={14} className="text-red-400" />
              <h2 className="font-serif-editorial text-white text-[20px] leading-tight">Fautes éliminatoires</h2>
            </div>
            <p className="text-white/65 text-[13px] mb-4 leading-relaxed">Coche les éventuelles fautes graves commises lors de tes dernières leçons. <strong className="text-red-300">Une seule suffit à invalider l'examen, peu importe le score global.</strong></p>
            <ul className="space-y-2">
              {ELIMINATOIRES.map((e, i) => (
                <li key={i}>
                  <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-white/10 bg-[#0a0a0b] p-3 hover:border-red-700/40 transition" data-testid={`eliminatoire-${i}`}>
                    <input
                      type="checkbox"
                      checked={!!eliminatoires[i]}
                      onChange={() => toggleEliminatoire(i)}
                      className="mt-0.5 w-4 h-4 accent-red-600 shrink-0"
                    />
                    <span className="text-white/80 text-[13.5px] leading-relaxed">{e}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Verdict */}
        {evaluated > 0 && (
          <section className={`rounded-2xl sm:rounded-3xl border-2 bg-gradient-to-br ${VERDICT_COLOR[verdict.color]} p-5 sm:p-7 lg:p-9 mb-7 sm:mb-10`} data-testid="verdict-section">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-black/40 flex items-center justify-center shrink-0">
                {verdict.status === 'admis' ? <Trophy size={28} /> : verdict.status === 'eliminatoire' ? <Lock size={28} /> : <ClipboardCheck size={28} />}
              </div>
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold font-mono-spec">Verdict provisoire · sur {evaluated}/{totalCriteres} critères évalués</div>
                <h2 className="font-serif-editorial text-white text-[28px] lg:text-[34px] leading-tight mt-1" data-testid="verdict-label">{verdict.label}</h2>
                <p className="text-white/85 text-[14px] mt-3 leading-relaxed">{verdict.desc}</p>
                {evaluated < totalCriteres && (
                  <p className="text-white/55 text-[12.5px] mt-3 italic">Évalue les {totalCriteres - evaluated} critère(s) restant(s) pour un verdict définitif.</p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between flex-wrap gap-3 mt-10 pt-6 border-t border-white/5">
          <button onClick={reset} data-testid="reset-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
            <RotateCcw size={14} /> Recommencer
          </button>
          <Link to="/cours/eleve/voiture" data-testid="back-cours-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold shadow-lg shadow-red-900/40 transition">
            <GraduationCap size={14} /> Réviser les 38 leçons REMC
          </Link>
        </div>

        <div className="mt-10 text-[11.5px] text-white/40 font-mono-spec inline-flex items-center gap-2">
          <ClipboardCheck size={11} /> Auto-évaluation indicative — l'examen réel reste à la main de ton enseignant et de l'examinateur certifié. codevoiture.fr
        </div>
      </section>
    </PageShell>
  );
}
